// Pattern voucher cards (Marble Suede, Halftone Pulse, Prism Cut), ported from the design's
// shader-card engine (WebGL2). Marble line width and halftone cell count scale with the card.
// Pure data: shared prelude + one `card()` per finish.

import type { PatternFinish } from 'content/gifting/voucherConfig';

export const PATTERN_VS =
  '#version 300 es\nin vec2 a;void main(){gl_Position=vec4(a,0.,1.);}';

export const PATTERN_PRE = `#version 300 es
precision highp float;
uniform vec2 uRes;uniform float uTime;uniform vec2 uMouse;uniform float uHover;uniform sampler2D uTex;uniform float uTexAsp;
out vec4 fragColor;
#define T uTime
#define PI 3.14159265
#define TAU 6.2831853
#define PX (1./uRes.y)
#define M ((uMouse-.5)*vec2(uRes.x/uRes.y,1.))
mat2 rot(float a){float c=cos(a),s=sin(a);return mat2(c,-s,s,c);}
float hash(vec2 p){vec3 p3=fract(vec3(p.xyx)*.1031);p3+=dot(p3,p3.yzx+33.33);return fract((p3.x+p3.y)*p3.z);}
vec2 hash2(vec2 p){vec3 p3=fract(vec3(p.xyx)*vec3(.1031,.1030,.0973));p3+=dot(p3,p3.yzx+33.33);return fract((p3.xx+p3.yz)*p3.zy);}
float hash1(float n){return fract(sin(n*12.9898)*43758.5453);}
float noise(vec2 p){vec2 i=floor(p),f=fract(p);vec2 u=f*f*f*(f*(f*6.-15.)+10.);
 float a=dot(hash2(i)*2.-1.,f),b=dot(hash2(i+vec2(1,0))*2.-1.,f-vec2(1,0)),c=dot(hash2(i+vec2(0,1))*2.-1.,f-vec2(0,1)),d=dot(hash2(i+1.)*2.-1.,f-1.);
 return .5+.75*mix(mix(a,b,u.x),mix(c,d,u.x),u.y);}
float fbm(vec2 p){float v=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);for(int i=0;i<5;i++){v+=a*noise(p);p=m*p;a*=.5;}return v;}
float fbm3(vec2 p){float v=0.,a=.5;mat2 m=mat2(1.6,1.2,-1.2,1.6);for(int i=0;i<3;i++){v+=a*noise(p);p=m*p;a*=.5;}return v/.875;}
vec3 pal(float t,vec3 a,vec3 b,vec3 c,vec3 d){return a+b*cos(TAU*(c*t+d));}
float smin(float a,float b,float k){float h=clamp(.5+.5*(b-a)/k,0.,1.);return mix(b,a,h)-k*h*(1.-h);}
float sdSeg(vec2 p,vec2 a,vec2 b){vec2 pa=p-a,ba=b-a;float h=clamp(dot(pa,ba)/dot(ba,ba),0.,1.);return length(pa-ba*h);}
float sdMark(vec2 p){
 const vec2 v[11]=vec2[11](vec2(.3942,.3684),vec2(-.3942,.3684),vec2(-.3942,.0842),vec2(-.2358,.0842),vec2(-.5,-.1584),vec2(-.3058,-.3684),vec2(-.0016,-.0932),vec2(.3058,-.3684),vec2(.5,-.1584),vec2(.2342,.0842),vec2(.3942,.0842));
 float d=dot(p-v[0],p-v[0]),s=1.;
 for(int i=0,j=10;i<11;j=i,i++){vec2 e=v[j]-v[i],w=p-v[i];vec2 b=w-e*clamp(dot(w,e)/dot(e,e),0.,1.);d=min(d,dot(b,b));
  bvec3 c=bvec3(p.y>=v[i].y,p.y<v[j].y,e.x*w.y>e.y*w.x);if(all(c)||all(not(c)))s*=-1.;}
 return s*sqrt(d);}
vec3 studio(vec3 r,vec3 top,vec3 hz,vec3 gr){
 vec3 c=r.y>0.?mix(hz,top,pow(r.y,.5)):mix(hz*.3,gr,pow(-r.y,.4));
 c+=1.3*exp(-pow((r.x+.45)*6.,2.))*smoothstep(-.1,.45,r.y);
 c+=.7*exp(-pow((r.x-.55)*9.,2.))*smoothstep(.15,.6,r.y);
 c+=2.*pow(max(dot(r,normalize(vec3(-.3,.5,.8))),0.),80.);
 return c;}
vec3 card(vec2 uv,vec2 p);
void main(){vec2 uv=gl_FragCoord.xy/uRes;vec2 p=(gl_FragCoord.xy-.5*uRes)/uRes.y;vec3 c=card(uv,p);c+=(hash(gl_FragCoord.xy+fract(T)*91.)-.5)/255.;fragColor=vec4(clamp(c,0.,1.),1.);}
uniform float uCells;uniform float uLine;
`;

export const PATTERN_FS: Record<PatternFinish, string> = {
  marble: `vec3 card(vec2 uv,vec2 p){
 float t=T*.04;vec2 q=p*1.2;
 vec2 w=vec2(fbm(q*1.3+t),fbm(q*1.3+vec2(3.1,7.7)-t));
 float f=fbm(q+2.4*w+vec2(t*.5,0.));
 float v=f*9.;float ln=abs(fract(v)-.5)/fwidth(v);float lines=1.-smoothstep(0.,uLine,ln);
 vec3 col=mix(vec3(.02,.33,.20),vec3(.10,.72,.46),smoothstep(.3,.75,f));
 col=mix(col,col*1.28,lines*.7);
 col*=.9+.18*hash(floor(gl_FragCoord.xy));
 col+=.15*uHover*exp(-length(p-M)*3.)*vec3(.5,1.,.7);
 return col;}`,
  halftone: `vec3 card(vec2 uv,vec2 p){
 float cs=1./uCells;vec2 id=floor(p/cs);vec2 cp=(id+.5)*cs;vec2 f=(p-cp)/cs;
 float dm=sdMark(cp/.62)*.62;float inM=smoothstep(.01,-.02,dm);
 float w=.5+.5*sin(length(cp)*18.-T*2.5);
 float near=uHover*exp(-length(cp-M)*7.);
 float r=min(.06+.1*w+.3*inM+.22*near,.48);
 float l=length(f);float aa=fwidth(l)*1.2;
 return mix(vec3(.07,.2,1.),vec3(1.),smoothstep(r+aa,r-aa,l));}`,
  prism: `vec3 card(vec2 uv,vec2 p){
 float t=T*.25;vec2 q=rot(-.5)*p;
 float w=q.x*1.5+.4*sin(q.y*2.+t)+.25*fbm(q*2.+t);
 float band=fract(w*.6-t*.15);
 vec3 spec=pal(band*1.2,vec3(.5),vec3(.5),vec3(1.),vec3(0.,.33,.67));
 float lum=smoothstep(0.,.5,band)*smoothstep(1.,.55,band);
 vec3 col=mix(vec3(.12,.13,.16),vec3(.97,.96,1.),lum*lum);
 col+=spec*exp(-pow((band-.62)*14.,2.))*.9;
 col+=vec3(1.,.55,.2)*exp(-pow((band-.3)*20.,2.))*.6;
 return col;}`,
};

/** Card base colour shown before the first frame (and without WebGL2). */
export const PATTERN_BG: Record<PatternFinish, string> = {
  marble: '#0a6b44',
  halftone: '#1238ff',
  prism: '#000',
};
